/**
 * shadcn-style Form — thin wrappers over react-hook-form's FormProvider /
 * Controller / FieldError, surfacing them as semantic <FormItem>/<FormLabel>/
 * <FormControl>/<FormMessage>/<FormDescription> primitives that compose with
 * the Label and Input we ship above.
 *
 * Usage:
 *   const form = useForm({ resolver: zodResolver(LoginSchema) });
 *   <Form {...form}>
 *     <FormField name="email" control={form.control} render={({ field }) => (
 *       <FormItem>
 *         <FormLabel>Email</FormLabel>
 *         <FormControl><Input {...field} /></FormControl>
 *         <FormMessage />
 *       </FormItem>
 *     )} />
 *   </Form>
 */
import * as React from 'react';
import * as LabelPrimitive from '@radix-ui/react-label';
import { Slot } from '@radix-ui/react-slot';
import { AlertCircle } from 'lucide-react';
import {
  Controller,
  type ControllerProps,
  type FieldPath,
  type FieldValues,
  FormProvider,
  useFormContext,
} from 'react-hook-form';

import { cn } from '@/lib/utils';
import { Label } from './label';

const Form = FormProvider;

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = { name: TName };

const FormFieldContext = React.createContext<FormFieldContextValue | undefined>(undefined);

function FormField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(props: ControllerProps<TFieldValues, TName>) {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  );
}

type FormItemContextValue = { id: string };

const FormItemContext = React.createContext<FormItemContextValue | undefined>(undefined);

const FormItem = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const id = React.useId();
    return (
      <FormItemContext.Provider value={{ id }}>
        <div ref={ref} className={cn('space-y-1.5', className)} {...props} />
      </FormItemContext.Provider>
    );
  },
);
FormItem.displayName = 'FormItem';

function useFormField() {
  const fieldContext = React.useContext(FormFieldContext);
  const itemContext = React.useContext(FormItemContext);
  const { getFieldState, formState } = useFormContext();
  if (!fieldContext) {
    throw new Error('useFormField must be used inside a <FormField>');
  }
  if (!itemContext) {
    throw new Error('useFormField must be used inside a <FormItem>');
  }
  const fieldState = getFieldState(fieldContext.name, formState);
  const { id } = itemContext;
  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  };
}

const FormLabel = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>
>(({ className, ...props }, ref) => {
  const { error, formItemId } = useFormField();
  return (
    <Label
      ref={ref}
      className={cn(error && 'text-destructive', className)}
      htmlFor={formItemId}
      {...props}
    />
  );
});
FormLabel.displayName = 'FormLabel';

const FormControl = React.forwardRef<
  React.ElementRef<typeof Slot>,
  React.ComponentPropsWithoutRef<typeof Slot>
>(({ ...props }, ref) => {
  const { error, formItemId, formDescriptionId, formMessageId } = useFormField();
  return (
    <Slot
      ref={ref}
      id={formItemId}
      aria-describedby={error ? `${formDescriptionId} ${formMessageId}` : formDescriptionId}
      aria-invalid={Boolean(error)}
      {...props}
    />
  );
});
FormControl.displayName = 'FormControl';

const FormDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => {
  const { formDescriptionId } = useFormField();
  return (
    <p
      ref={ref}
      id={formDescriptionId}
      className={cn('text-xs text-muted', className)}
      {...props}
    />
  );
});
FormDescription.displayName = 'FormDescription';

/**
 * FormMessage — Wave-5 fix 5.x.8 / devil-advocate F8, UI A-2.
 *
 * Two a11y improvements over the prior implementation:
 *
 *   1. `role="alert"` (with implicit aria-live="assertive") is set when
 *      the message represents an actual validation error so AT users
 *      hear the failure as soon as it appears instead of having to
 *      navigate back to the field. We deliberately gate the role on
 *      `error` (not on `body`) so non-error description text rendered
 *      via `<FormMessage>children</FormMessage>` does not make every
 *      screen-reader user re-hear unchanged copy.
 *
 *   2. `<AlertCircle>` icon prefix on error state. WCAG 1.3.1 / 1.4.1
 *      forbid colour-only signaling, so an icon is required. The icon
 *      is `aria-hidden` since the role + visible message text already
 *      convey the error semantically.
 */
const FormMessage = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, children, ...props }, ref) => {
  const { error, formMessageId } = useFormField();
  const body = error ? String(error?.message ?? '') : children;
  if (!body) return null;
  const isError = Boolean(error);
  return (
    <p
      ref={ref}
      id={formMessageId}
      // role=alert only when conveying an error; aria-live=polite would
      // also work but role=alert (assertive) is the right pick for
      // submit-time validation failures per WAI-ARIA APG.
      role={isError ? 'alert' : undefined}
      data-error={isError ? 'true' : undefined}
      className={cn(
        'flex items-start gap-1 text-xs font-medium',
        isError ? 'text-destructive' : 'text-muted',
        className,
      )}
      {...props}
    >
      {isError ? (
        <AlertCircle
          aria-hidden="true"
          // Match the line-height of the text so the icon sits on the
          // baseline cleanly. 14px is the right hit for 12px text.
          className="mt-[1px] h-3.5 w-3.5 shrink-0"
        />
      ) : null}
      <span>{body}</span>
    </p>
  );
});
FormMessage.displayName = 'FormMessage';

export {
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
  FormField,
  useFormField,
};
